import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { TZ_SHORT, getCourseScheduleEntries, getScheduleForDay, formatCourseSchedule, getJoinLink } from '../../utils/courseSchedule';

const DAY_MAP = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6
};

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const FREQ_SHORT = {
  monthly: '/mo',
  quarterly: '/quarter',
  semi_annual: '/6 mo',
  annual: '/yr',
  one_time: ' one-time',
  'one-time': ' one-time'
};

// Returns { date, dayName, todaySkipped } for the next REAL class, or null.
// Skips dates that are studio holidays or specifically cancelled for this course.
// `todaySkipped` is true if today was a class day for this course but had to be
// skipped (cancelled or holiday) — used to show a small "Today's class is cancelled" advisory.
const getNextClass = (course, studio) => {
  const entries = getCourseScheduleEntries(course);
  if (!entries.length) return null;
  if (course.type !== 'long_course' && course.type !== 'block') return null;

  const courseDayIdxs = entries.map(e => DAY_MAP[e.day]).filter(i => i !== undefined);
  const cancelledDates = course.schedule?.cancelledDates || [];
  const studioHolidays = studio?.holidays || [];
  const sameLocalAsStored = (stored, local) => {
    if (!stored || !local) return false;
    const s = stored instanceof Date ? stored : new Date(stored);
    return s.getUTCFullYear() === local.getFullYear()
      && s.getUTCMonth() === local.getMonth()
      && s.getUTCDate() === local.getDate();
  };
  const isBlocked = (d) =>
    cancelledDates.some(cd => sameLocalAsStored(cd, d))
    || studioHolidays.some(h => sameLocalAsStored(h.date, d));

  const now = new Date();
  const today = now.getDay();
  let todaySkipped = false;

  for (let offset = 0; offset < 28; offset++) {
    const checkDay = (today + offset) % 7;
    if (!courseDayIdxs.includes(checkDay)) continue;
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + offset);
    candidate.setHours(0, 0, 0, 0);
    if (isBlocked(candidate)) {
      if (offset === 0) todaySkipped = true;
      continue;
    }
    return { date: candidate, dayName: DAY_NAMES[checkDay], todaySkipped };
  }
  return null;
};

const formatNextClass = (course, studio) => {
  const next = getNextClass(course, studio);
  if (!next) return null;

  const { date: nextDate, dayName } = next;
  const now = new Date();
  const isToday = nextDate.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = nextDate.toDateString() === tomorrow.toDateString();

  const dayLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : nextDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const tz = TZ_SHORT[course.schedule?.timezone] || '';
  // Use the time for THIS specific day (per-day times) — falls back to legacy if needed
  const dayEntry = getScheduleForDay(course, dayName);
  const startTime = dayEntry?.startTime || course.schedule?.startTime || '';
  const time = startTime ? `at ${startTime} ${tz}`.trim() : '';

  return { dayLabel, time, isToday, isTomorrow, date: nextDate, todaySkipped: next.todaySkipped };
};

const StudentOverview = () => {
  const { user, selectedProfile } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [materialsMap, setMaterialsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get('/enroll/my');
        const all = data.enrollments || [];
        // Filter to the selected profile (parent view) — show only enrollments
        // owned by the currently viewed profile. Single-student users have a
        // selectedProfile equal to themselves, so this is a no-op for them.
        const enrs = selectedProfile
          ? all.filter(e => (e.student?._id || e.student)?.toString() === selectedProfile._id.toString())
          : all;
        setEnrollments(enrs);

        // Fetch materials for active enrollments
        const active = enrs.filter(e => e.status === 'active' && e.course);
        const materialsResults = await Promise.allSettled(
          active.map(e => api.get(`/materials/student/${e.course._id}`))
        );

        const mMap = {};
        active.forEach((e, i) => {
          if (materialsResults[i].status === 'fulfilled') {
            mMap[e.course._id] = materialsResults[i].value.data;
          }
        });
        setMaterialsMap(mMap);
      } catch (err) {
        console.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedProfile?._id]);

  if (loading) {
    return <div className="text-gray-500">Loading dashboard...</div>;
  }

  const activeEnrollments = enrollments.filter(e => e.status === 'active' && e.course);
  const pastEnrollments = enrollments.filter(e => e.status !== 'active' && e.course);

  // Find the soonest next class across all courses
  let soonestClass = null;
  let soonestEnrollment = null;
  activeEnrollments.forEach(e => {
    const next = formatNextClass(e.course, e.studio);
    if (next && (!soonestClass || next.date < soonestClass.date)) {
      soonestClass = next;
      soonestEnrollment = e;
    }
  });

  // Aggregate attendance for this month
  const totalAttended = activeEnrollments.reduce((sum, e) => sum + (e.totalClassesAttended || 0), 0);
  const totalMissed = activeEnrollments.reduce((sum, e) => sum + (e.totalClassesMissed || 0), 0);
  const totalClasses = totalAttended + totalMissed;
  const attendanceRate = totalClasses > 0 ? Math.round((totalAttended / totalClasses) * 100) : 0;

  // Recently unlocked materials (across all courses)
  const recentMaterials = [];
  const nextToUnlock = [];
  activeEnrollments.forEach(e => {
    const mData = materialsMap[e.course?._id];
    if (!mData?.materials) return;
    mData.materials.forEach(week => {
      if (week.unlocked) {
        recentMaterials.push({
          courseName: e.course.name,
          courseId: e.course._id,
          week: week.week,
          title: week.title || `Week ${week.week}`,
          videos: week.videos || []
        });
      } else if (nextToUnlock.length < 2) {
        const classesNeeded = week.week - (mData.attendanceCount || 0);
        if (classesNeeded > 0) {
          nextToUnlock.push({
            courseName: e.course.name,
            courseId: e.course._id,
            week: week.week,
            title: week.title || `Week ${week.week}`,
            classesNeeded
          });
        }
      }
    });
  });
  // Show only last 3 unlocked
  const recentUnlocked = recentMaterials.slice(-3).reverse();

  // Upcoming holidays (studio + US)
  const now = new Date();
  const year = now.getFullYear();
  const studioHols = [];
  activeEnrollments.forEach(e => {
    (e.studio?.holidays || []).forEach(h => {
      const d = new Date(h.date);
      if (d >= now) studioHols.push({ date: d, name: h.name, type: 'studio' });
    });
  });
  // US holidays
  const usHols = [
    { date: new Date(year, 0, 1), name: "New Year's Day" },
    { date: new Date(year, 0, 1 + ((1 - new Date(year, 0, 1).getDay() + 7) % 7) + 14), name: 'MLK Day' },
    { date: new Date(year, 4, 31 - ((new Date(year, 4, 31).getDay() + 6) % 7)), name: 'Memorial Day' },
    { date: new Date(year, 6, 4), name: 'Independence Day' },
    { date: new Date(year, 8, 1 + ((1 - new Date(year, 8, 1).getDay() + 7) % 7)), name: 'Labor Day' },
    { date: new Date(year, 10, 1 + ((4 - new Date(year, 10, 1).getDay() + 7) % 7) + 21), name: 'Thanksgiving' },
    { date: new Date(year, 11, 25), name: 'Christmas Day' },
  ].filter(h => h.date >= now).map(h => ({ ...h, type: 'us' }));

  const uniqueHolidays = [...new Map([...studioHols, ...usHols].map(h => [h.name, h])).values()]
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  // Upcoming assessments across all active enrollments
  const upcomingAssessments = activeEnrollments
    .flatMap(e =>
      (e.course?.assessments || [])
        .filter(a => a.date)
        .map(a => ({
          id: a._id,
          title: a.title,
          type: a.type || 'assessment',
          date: new Date(a.date),
          courseId: e.course._id,
          courseName: e.course.name
        }))
    )
    .filter(a => a.date >= now)
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  // Recent graded results across all active enrollments — only past assessments
  // that have a myGrade payload (server-filtered to this student).
  const recentResults = activeEnrollments
    .flatMap(e =>
      (e.course?.assessments || [])
        .filter(a => a.date && new Date(a.date).getTime() < now.getTime() && a.myGrade)
        .filter(a => {
          const m = a.myGrade;
          return (m.marks !== null && m.marks !== undefined) || (m.grade && m.grade.length > 0) || (m.comments && m.comments.length > 0);
        })
        .map(a => ({
          id: a._id,
          title: a.title,
          type: a.type || 'assessment',
          date: new Date(a.date),
          courseId: e.course._id,
          courseName: e.course.name,
          myGrade: a.myGrade
        }))
    )
    .sort((a, b) => b.date - a.date)
    .slice(0, 4);

  // Next payment date
  const nextPayment = activeEnrollments.find(e => e.currentPeriodEnd);
  const nextPaymentDate = nextPayment?.currentPeriodEnd ? new Date(nextPayment.currentPeriodEnd) : null;

  // Determine greeting based on new student vs returning.
  // Greet by the currently viewed profile's name (falls back to account holder
  // name, then 'there'). Always use the name — never the email — even when the
  // account holder's name field matches their email string (legacy auto-create).
  const isNewStudent = totalClasses < 4;
  const rawName = selectedProfile?.name || user?.name || '';
  const nameIsEmail = rawName.includes('@');
  const displayName = nameIsEmail ? (rawName.split('@')[0].replace(/[._+]/g, ' ') || 'there') : rawName;
  const firstName = displayName.split(' ')[0] || 'there';

  return (
    <div className="flex gap-8">
      {/* Main Column */}
      <div className="flex-1 min-w-0">
        {/* Welcome + Next Class */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {isNewStudent ? `Welcome, ${firstName}!` : `Hey, ${firstName}`}
          </h1>
          {isNewStudent && (
            <p className="text-sm text-gray-500 mb-4">Here's everything you need to get started.</p>
          )}
        </div>

        {/* Next Class Card */}
        {soonestClass && soonestEnrollment && (
          <div className={`rounded-xl border shadow-sm p-5 mb-6 ${
            soonestClass.isToday ? 'bg-brand-50 border-brand-200' : 'bg-white border-gray-100'
          }`}>
            <div>
              <div className="text-xs font-semibold text-brand-500 uppercase tracking-wide mb-1">
                {soonestClass.isToday ? 'Class Today' : 'Next Class'}
                {selectedProfile && !selectedProfile.isSelf && (
                  <span className="text-gray-400 font-medium normal-case tracking-normal"> for {selectedProfile.name}</span>
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900">{soonestEnrollment.course.name}</h3>
              {soonestClass.todaySkipped && (
                <p className="text-xs text-gray-500 mt-1 italic">
                  Today's class is cancelled — showing your next one.
                </p>
              )}
              <p className="text-sm text-gray-600 mt-1">
                {soonestClass.dayLabel} {soonestClass.time}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => navigate('/student/scan')}
                  className="inline-flex items-center gap-1.5 bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                  </svg>
                  Scan Attendance
                </button>
                {(() => {
                  const joinUrl = getJoinLink(soonestEnrollment.course);
                  return joinUrl && (
                    <a
                      href={joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                      Join class
                    </a>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Course Cards */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">My Courses</h2>
          {activeEnrollments.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
              <p className="text-gray-500">No active courses. Enroll in a course to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {activeEnrollments.map(enrollment => {
                const course = enrollment.course;
                const mData = materialsMap[course._id];
                const unlockedCount = mData?.materials?.filter(m => m.unlocked).length || 0;
                const totalWeeks = mData?.totalWeeks || mData?.materials?.length || 0;
                const attended = enrollment.totalClassesAttended || 0;
                const missed = enrollment.totalClassesMissed || 0;
                const rate = (attended + missed) > 0 ? Math.round((attended / (attended + missed)) * 100) : 0;
                const scheduleStr = formatCourseSchedule(course, { short: true, includeEndTime: false });

                return (
                  <div
                    key={enrollment._id}
                    onClick={() => navigate(`/student/course/${course._id}`)}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all cursor-pointer p-4"
                  >
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{course.name}</h3>
                    {scheduleStr && (
                      <p className="text-xs text-gray-500 mb-3">{scheduleStr}</p>
                    )}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Attendance</span>
                        <span className="font-medium text-gray-900">{attended} classes ({rate}%)</span>
                      </div>
                      {totalWeeks > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Materials</span>
                          <span className="font-medium text-gray-900">{unlockedCount}/{totalWeeks} unlocked</span>
                        </div>
                      )}
                    </div>
                    {/* Quick actions */}
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50">
                      {(() => {
                        const joinUrl = getJoinLink(course);
                        return joinUrl && (
                          <a
                            href={joinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-xs font-medium text-brand-500 hover:text-brand-600"
                          >
                            Join class
                          </a>
                        );
                      })()}
                      {course.googleClassroom?.courseUrl && (
                        <a
                          href={course.googleClassroom.courseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-xs font-medium text-brand-500 hover:text-brand-600"
                        >
                          Classroom
                        </a>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/student/course/${course._id}`); }}
                        className="text-xs font-medium text-gray-500 hover:text-gray-700"
                      >
                        Materials
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Watch Again — recently unlocked materials */}
        {recentUnlocked.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Watch Again</h2>
            <div className="space-y-2">
              {recentUnlocked.map((m, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/student/course/${m.courseId}`)}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:border-brand-200 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">Week {m.week}: {m.title}</p>
                      <p className="text-xs text-gray-500">{m.courseName} {m.videos.length > 0 ? `\u00b7 ${m.videos.length} video${m.videos.length !== 1 ? 's' : ''}` : ''}</p>
                    </div>
                    <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            {/* Next to unlock */}
            {nextToUnlock.length > 0 && (
              <div className="mt-3 px-4 py-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Next to Unlock</p>
                {nextToUnlock.map((m, i) => (
                  <p key={i} className="text-sm text-gray-600">
                    <span className="font-medium">{m.title}</span>
                    <span className="text-gray-400"> — attend {m.classesNeeded} more class{m.classesNeeded !== 1 ? 'es' : ''}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent Results — mobile only (desktop has sidebar widget) */}
        {recentResults.length > 0 && (
          <div className="mb-6 lg:hidden">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent Results</h2>
            <div className="space-y-2">
              {recentResults.map(r => {
                const m = r.myGrade;
                const display = r.type === 'exam'
                  ? (m.grade || '—')
                  : (m.marks !== null && m.marks !== undefined ? `${m.marks}/100` : '—');
                return (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/student/course/${r.courseId}`)}
                    className="w-full bg-emerald-50 border border-emerald-200 rounded-xl p-4 hover:bg-emerald-100 transition-colors text-left"
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-[3.25rem] px-2 py-1 rounded-lg bg-white border border-emerald-300 text-emerald-700 text-sm font-bold text-center flex-shrink-0">
                        {display}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{r.title}</p>
                        <p className="text-xs text-gray-600 truncate">{r.courseName}</p>
                        <p className="text-xs text-emerald-700 font-medium mt-0.5">
                          {r.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        {m.comments && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{m.comments}</p>
                        )}
                      </div>
                      <svg className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming Assessments — mobile only (desktop has sidebar widget) */}
        {upcomingAssessments.length > 0 && (
          <div className="mb-6 lg:hidden">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Upcoming Assessments</h2>
            <div className="space-y-2">
              {upcomingAssessments.map(a => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/student/course/${a.courseId}`)}
                  className="w-full bg-blue-50 border border-blue-200 rounded-xl p-4 hover:bg-blue-100 transition-colors text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{a.title}</p>
                      <p className="text-xs text-gray-600 truncate">{a.courseName}</p>
                      <p className="text-xs text-blue-700 font-medium mt-0.5">
                        {a.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Past Enrollments */}
        {pastEnrollments.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Past Courses</h2>
            <div className="space-y-2">
              {pastEnrollments.map(enrollment => (
                <div
                  key={enrollment._id}
                  onClick={() => navigate(`/student/course/${enrollment.course._id}`)}
                  className="bg-white rounded-lg border border-gray-100 p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-700">{enrollment.course.name}</p>
                    <p className="text-xs text-gray-400">{enrollment.totalClassesAttended || 0} classes attended</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    enrollment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    enrollment.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                    enrollment.status === 'payment_failed' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {enrollment.status === 'completed' ? 'Completed' :
                     enrollment.status === 'cancelled' ? 'Cancelled' :
                     enrollment.status === 'payment_failed' ? 'Payment Failed' :
                     enrollment.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar */}
      <div className="w-72 flex-shrink-0 space-y-5 hidden lg:block">
        {/* To-Do */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">To Do</h3>
          <div className="space-y-3">
            {recentUnlocked.length > 0 ? (
              recentUnlocked.slice(0, 2).map((m, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 leading-tight">Watch: {m.title}</p>
                    <p className="text-xs text-gray-400">{m.courseName}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">You're all caught up!</p>
            )}

            {/* Set up password — links to profile */}
            <div
              className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
              onClick={() => navigate('/student/profile')}
            >
              <div className="w-5 h-5 rounded bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-700 leading-tight">Update your password</p>
                <p className="text-xs text-gray-400">Go to Profile</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Holidays */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Upcoming Holidays</h3>
          <div className="space-y-2.5">
            {uniqueHolidays.length > 0 ? (
              uniqueHolidays.map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.type === 'studio' ? 'bg-brand-500' : 'bg-orange-400'}`} />
                  <div>
                    <p className="text-sm text-gray-700">{h.name}</p>
                    <p className="text-xs text-gray-400">
                      {h.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {h.type === 'studio' && ' — No class'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">No upcoming holidays</p>
            )}
          </div>
          <button
            onClick={() => navigate('/student/calendar')}
            className="text-xs text-brand-500 hover:text-brand-600 font-medium mt-3"
          >
            View Calendar
          </button>
        </div>

        {/* Upcoming Assessments */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Upcoming Assessments</h3>
          {upcomingAssessments.length > 0 ? (
            <div className="space-y-2.5">
              {upcomingAssessments.map(a => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/student/course/${a.courseId}`)}
                  className="flex items-start gap-2 w-full text-left hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition-colors"
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0 bg-blue-500 mt-1.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-700 truncate">{a.title}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {a.courseName} · {a.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No upcoming assessments</p>
          )}
        </div>

        {/* Recent Results */}
        {recentResults.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Results</h3>
            <div className="space-y-3">
              {recentResults.map(r => {
                const m = r.myGrade;
                const display = r.type === 'exam'
                  ? (m.grade || '—')
                  : (m.marks !== null && m.marks !== undefined ? `${m.marks}/100` : '—');
                return (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/student/course/${r.courseId}`)}
                    className="w-full text-left hover:bg-gray-50 -mx-2 px-2 py-2 rounded transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 text-emerald-700 text-sm font-bold min-w-[3.5rem] text-center">
                        {display}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-800 font-medium truncate">{r.title}</p>
                        <p className="text-xs text-gray-400 truncate">
                          {r.courseName} · {r.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                        {m.comments && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{m.comments}</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Attendance Snapshot */}
        {totalClasses > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Attendance</h3>
            <div className="text-3xl font-bold text-brand-500 mb-1">{attendanceRate}%</div>
            <p className="text-xs text-gray-500 mb-3">{totalAttended} of {totalClasses} classes attended</p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-500 rounded-full h-2 transition-all"
                style={{ width: `${attendanceRate}%` }}
              />
            </div>
            {/* Streak */}
            {activeEnrollments.some(e => e.consecutiveAbsences === 0 && e.totalClassesAttended > 0) && (
              <p className="text-xs text-green-600 mt-2 font-medium">
                No recent absences — keep it up!
              </p>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default StudentOverview;
